import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_DESCRIPTION,
  SITE_NAME,
  buildCanonicalUrl,
  formatPageTitle,
  resolveMetaDescription,
} from "@/lib/siteUrl";

interface PageMetaProps {
  title?: string;
  description?: string | null;
  image?: string | null;
  /** URL canônica absoluta; se omitida, usa origem pública + pathname atual */
  url?: string;
  /** false = noindex (busca, 404, etc.) */
  indexable?: boolean;
  /** Open Graph type — use "article" em matérias */
  ogType?: "website" | "article";
  /** JSON-LD estruturado (NewsArticle etc.) */
  jsonLd?: Record<string, unknown> | null;
  publishedTime?: string | null;
  modifiedTime?: string | null;
}

export function PageMeta({
  title,
  description,
  image,
  url,
  indexable = true,
  ogType = "website",
  jsonLd = null,
  publishedTime,
  modifiedTime,
}: PageMetaProps) {
  const { pathname } = useLocation();
  const fullTitle = formatPageTitle(title);
  const desc = resolveMetaDescription(description, DEFAULT_DESCRIPTION);
  const canonical = buildCanonicalUrl(pathname, url);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {indexable ? null : <meta name="robots" content="noindex,follow" />}
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      {image ? <meta property="og:image" content={image} /> : null}
      {publishedTime ? <meta property="article:published_time" content={publishedTime} /> : null}
      {modifiedTime ? <meta property="article:modified_time" content={modifiedTime} /> : null}

      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      {image ? <meta name="twitter:image" content={image} /> : null}

      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}

      <link rel="icon" href="/favicon-48x48.png?v=3" sizes="48x48" type="image/png" />
    </Helmet>
  );
}
